import {
  getNextStatusAfterApproval,
  pickTaskForFlowRole,
} from "@features/gatepass/components/flow/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type {
  CompleteGatepassPayload,
  GatePassApiResponseItem,
  GatePassTask,
} from "@services/gatepass/types";
import { completeGatepassById } from "@services/gatepassDataApi";
import type { UserResponse } from "@services/user/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

function skipSponsorCompanyApprovalFromRecord(data: GatePassApiResponseItem): boolean {
  const sponsor = String(data.sponsorCompany ?? "").trim();
  const submittedBy = String(data.submittedBy ?? "").trim();
  const submittedByRole = String(data.submittedByRole ?? "")
    .trim()
    .toLowerCase();
  const isVendorMasterRequester = submittedByRole === "vendor-master";

  return (
    sponsor !== "" && submittedBy !== "" && sponsor === submittedBy && !isVendorMasterRequester
  );
}

/** Update cached preview so badge + flow buttons react immediately; server fetch reconciles. */
function applyOptimisticPreviewAfterComplete(
  prev: GatePassApiResponseItem,
  payload: CompleteGatepassPayload,
  user: UserResponse | null,
): GatePassApiResponseItem {
  const now = new Date().toISOString();
  const attachments = payload.attachments ?? [];
  const decisionReason = payload.reason ?? payload.notes ?? null;
  const task = pickTaskForFlowRole<GatePassTask>(
    prev.status,
    prev.task ?? undefined,
    prev.tasks ?? undefined,
  );

  const nextStatus =
    payload.status === "REJECTED"
      ? "REJECTED"
      : getNextStatusAfterApproval(prev.status, task?.role, {
          skipSponsorCompanyApproval: skipSponsorCompanyApprovalFromRecord(prev),
        });

  const optimisticHistoryEntry: NonNullable<GatePassApiResponseItem["tasksHistory"]>[number] = {
    id: String(task?.taskId ?? `optimistic-${payload.status.toLowerCase()}-${now}`),
    status: "COMPLETED",
    owner: task?.claimOwnerName ?? undefined,
    taskDecision: {
      status: payload.status,
      reason: decisionReason,
      timestamp: now,
      attachments,
      submittedBy: user?.name,
      submittedByRole: user?.roles[0],
    },
  };

  return {
    ...prev,
    status: nextStatus,
    tasksHistory: [...(prev.tasksHistory ?? []), optimisticHistoryEntry],
  };
}

const getCompleteGatepassAction = (payload: CompleteGatepassPayload): string => {
  if (payload.status === "APPROVED") return "approve_gatepass";
  if (payload.status === "REJECTED") return "reject_gatepass";
  return "complete_gatepass";
};

export const useCompleteGatepass = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: () => void | Promise<void>;
  handleError: (error: Error) => void;
}) => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const completeGatepassMutation = useMutation({
    mutationFn: (variables: Parameters<typeof completeGatepassById>[0]) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "GatepassDetails",
          action: getCompleteGatepassAction(variables.payload),
          entityType: "gatepass",
        },
        (transactionId) =>
          completeGatepassById({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: async (_data, variables) => {
      const previewKey = ["gatepass-request-preview", variables.id] as const;

      const optimisticPreview = queryClient.setQueryData<GatePassApiResponseItem>(
        previewKey,
        (prev) =>
          prev ? applyOptimisticPreviewAfterComplete(prev, variables.payload, user) : prev,
      );

      await queryClient.invalidateQueries({ queryKey: ["gatepasses"], exact: false });
      await queryClient.invalidateQueries({
        queryKey: previewKey,
        exact: true,
        refetchType: "none",
      });

      if (optimisticPreview) {
        await queryClient.refetchQueries({ queryKey: previewKey, exact: true });
        queryClient.setQueryData<GatePassApiResponseItem>(previewKey, (refetchedPreview) =>
          refetchedPreview
            ? { ...refetchedPreview, status: optimisticPreview.status }
            : refetchedPreview,
        );
      }

      await Promise.resolve(handleSuccess());
    },
    onError: async (error: Error, variables) => {
      const previewKey = ["gatepass-request-preview", variables.id] as const;
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"], exact: false });
      await queryClient.invalidateQueries({
        queryKey: previewKey,
        exact: true,
      });

      handleError(error);
    },
  });

  const completeGatepass = async ({
    id,
    service,
    payload,
  }: {
    id: string;
    service: string;
    payload: CompleteGatepassPayload;
  }) => {
    completeGatepassMutation.mutate({
      id,
      service,
      payload,
    });
  };

  return {
    completeGatepass,
    isPending: completeGatepassMutation.isPending,
  };
};
